import React, { useEffect, Fragment, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Nav, Navbar, Row, Col } from 'react-bootstrap';
import { navbarBreakPoint, topNavbarBreakpoint } from 'config';
import Flex from 'components/common/Flex';
import Logo from 'components/common/Logo';
import NavbarVerticalMenu from './NavbarVerticalMenu';
import ToggleButton from './ToggleButton';
import routes from 'routes/siteMaps';
import { capitalize } from 'helpers/utils';
import { getUserAccesses } from 'helpers/access';
import NavbarTopDropDownMenus from 'components/navbar/top/NavbarTopDropDownMenus';
import PurchaseCard from './PurchaseCard';
import bgNavbar from 'assets/img/generic/bg-navbar.png';
import { useAppContext } from 'providers/AppProvider';
import { ProjectContext } from 'contexts/ProjectContext';

// Рекурсивный фильтр меню по access-IDs оператора (из JWT claim `r`).
// Видимость пункта:
//   - leaf (нет children): нет requiredAccess → видим; иначе видим только
//     если access-ID есть в Set userAccesses.
//   - parent: фильтруем children рекурсивно; родитель видим только если
//     остался хоть один видимый ребёнок (то есть не пустой).
// Раньше sidebar показывал всё → оператор без projectList видел «Проекты»
// и открывал страницу (бэкенд защищал 403, но пункт зря светился).
const filterByAccess = (items, userAccesses) => {
  const result = [];
  for (const item of items) {
    if (Array.isArray(item.children) && item.children.length > 0) {
      const visibleChildren = filterByAccess(item.children, userAccesses);
      if (visibleChildren.length === 0) continue;
      result.push({ ...item, children: visibleChildren });
    } else {
      if (item.requiredAccess && !userAccesses.has(item.requiredAccess)) continue;
      result.push(item);
    }
  }
  return result;
};

const NavbarVertical = () => {
  const projectContext = useContext(ProjectContext);
  const projectData = projectContext?.currentProject?.project_data || {};
  const enabledMenu = projectData.enabled_menu;
  const enabledSubmenu = projectData.enabled_submenu || {};

  // NAV-2 (v67): подписка на смену token в localStorage. Если token
  // обновляется silent refresh (другая вкладка / в фоне) и в нём изменились
  // claims.r — sidebar пересчитывает access-фильтр без перезагрузки страницы.
  // Раньше фильтр кешировался по [enabledMenu, enabledSubmenu] — token не
  // был в deps, изменения прав не подхватывались до remount.
  const [tokenVersion, setTokenVersion] = useState(() => localStorage.getItem('token') || '');
  useEffect(() => {
    // storage-событие срабатывает только в ДРУГИХ вкладках. Silent refresh в
    // ТЕКУЩЕЙ вкладке (axios-интерсептор перезаписал token) его не вызывает —
    // поэтому ловим ещё и кастомное 'auth-token-refreshed', которое диспатчит
    // apiService после успешного refresh. Иначе sidebar держал бы старый набор
    // прав до remount, и при протухании токена меню «скакало».
    const sync = () => setTokenVersion(localStorage.getItem('token') || '');
    const onStorage = e => {
      if (e.key === 'token') sync();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('auth-token-refreshed', sync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth-token-refreshed', sync);
    };
  }, []);

  const filteredRoutes = useMemo(() => {
    let list = routes;
    if (enabledMenu && Array.isArray(enabledMenu) && enabledMenu.length > 0) {
      const set = new Set(enabledMenu);
      list = routes.filter(route => set.has(route.label));
    }
    const projectFiltered = list.map(route => {
      const sub = enabledSubmenu[route.label];
      const children =
        sub && Array.isArray(sub) && sub.length > 0
          ? (route.children || []).filter(c => sub.includes(c.name))
          : route.children || [];
      return { ...route, children };
    });

    // Access-фильтр поверх per-project: дропаем пункты, на которые у
    // оператора нет прав; секции без видимых детей не рендерим вовсе.
    //
    // FAIL-OPEN (как hasAccess, v60.2): если набор доступов ПУСТ (токен
    // пропал/обновился без claim `r`/истёк) — НЕ фильтруем, показываем всё.
    // Иначе при любом пересчёте useMemo с пустым токеном меню «схлопывалось»
    // до Диаграмм/Поддержки/Ченджлога, и оператору приходилось перелогиниваться,
    // чтобы вернуть левое меню. Бэкенд всё равно защищает данные через checkAccess.
    const userAccesses = getUserAccesses();
    if (userAccesses.size === 0) {
      return projectFiltered.filter(section => (section.children || []).length > 0);
    }
    return projectFiltered
      .map(section => ({
        ...section,
        children: filterByAccess(section.children || [], userAccesses)
      }))
      .filter(section => (section.children || []).length > 0);
    // tokenVersion в deps — для пересчёта при silent token refresh
    // (см. useEffect storage listener выше). filterByAccess внутри
    // вызывает getUserAccesses() который читает actual localStorage.
  }, [enabledMenu, enabledSubmenu, tokenVersion]);

  const {
    config: {
      navbarPosition,
      navbarStyle,
      isNavbarVerticalCollapsed,
      showBurgerMenu
    }
  } = useAppContext();

  const HTMLClassList = document.getElementsByTagName('html')[0].classList;

  useEffect(() => {
    if (isNavbarVerticalCollapsed) {
      HTMLClassList.add('navbar-vertical-collapsed');
    } else {
      HTMLClassList.remove('navbar-vertical-collapsed');
    }
    return () => {
      HTMLClassList.remove('navbar-vertical-collapsed-hover');
    };
  }, [isNavbarVerticalCollapsed, HTMLClassList]);

  //Control mouseEnter event
  let time = null;
  const handleMouseEnter = () => {
    if (isNavbarVerticalCollapsed) {
      time = setTimeout(() => {
        HTMLClassList.add('navbar-vertical-collapsed-hover');
      }, 100);
    }
  };
  const handleMouseLeave = () => {
    clearTimeout(time);
    HTMLClassList.remove('navbar-vertical-collapsed-hover');
  };

  const NavbarLabel = ({ label }) => (
    <Nav.Item as="li">
      <Row className="mt-3 mb-2 navbar-vertical-label-wrapper">
        <Col xs="auto" className="navbar-vertical-label navbar-vertical-label">
          {label}
        </Col>
        <Col className="ps-0">
          <hr className="mb-0 navbar-vertical-divider"></hr>
        </Col>
      </Row>
    </Nav.Item>
  );

  return (
    <Navbar
      expand={navbarBreakPoint}
      className={classNames('navbar-vertical', {
        [`navbar-${navbarStyle}`]: navbarStyle !== 'transparent'
      })}
      variant="light"
    >
      <Flex alignItems="center">
        <ToggleButton />
        <Logo at="navbar-vertical" textClass="text-primary" width={40} />
      </Flex>
      <Navbar.Collapse
        in={showBurgerMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          backgroundImage:
            navbarStyle === 'vibrant'
              ? `linear-gradient(-45deg, rgba(0, 160, 255, 0.86), #0048a2),url(${bgNavbar})`
              : 'none'
        }}
      >
        <div className="navbar-vertical-content scrollbar">
          <Nav className="flex-column" as="ul">
            {filteredRoutes.map(route => (
              <Fragment key={route.label}>
                {!route.labelDisable && (
                  <NavbarLabel label={capitalize(route.label)} />
                )}
                <NavbarVerticalMenu routes={route.children} />
              </Fragment>
            ))}
          </Nav>

          <>
            {navbarPosition === 'combo' && (
              <div className={`d-${topNavbarBreakpoint}-none`}>
                <div className="navbar-vertical-divider">
                  <hr className="navbar-vertical-hr my-2" />
                </div>
                <Nav navbar>
                  <NavbarTopDropDownMenus />
                </Nav>
              </div>
            )}
            <PurchaseCard />
          </>
        </div>
      </Navbar.Collapse>
    </Navbar>
  );
};

NavbarVertical.propTypes = {
  label: PropTypes.string
};

export default NavbarVertical;
